import Editor, { loader } from "@monaco-editor/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Braces, Code2, Eye, FileWarning, Pencil, Save } from "lucide-react";
import type { WorkspaceFile } from "../types";
import { languageForPath } from "../lib/path";
import { monaco } from "../lib/monaco";
import { HtmlPreview } from "./HtmlPreview";

loader.config({ monaco });

function BinaryPreview({ file }: { file: WorkspaceFile }) {
  const url = useMemo(() => file.mimeType.startsWith("image/") ? URL.createObjectURL(new Blob([file.content as ArrayBuffer], { type: file.mimeType })) : null, [file]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return <div className="binary-preview">{url ? <img src={url} alt={file.path} /> : <FileWarning size={34} />}<strong>{file.path}</strong><span>Fichier binaire · {(file.size / 1024).toFixed(1)} Kio</span><p>Il sera conservé à l’identique dans l’export ZIP.</p></div>;
}

export function EditorPane({ file, files, readOnly, previewMode, previewVersion, onPreviewModeChange, onSave, onRename, onDirtyChange }: {
  file?: WorkspaceFile; files: WorkspaceFile[]; readOnly: boolean; previewMode: boolean; previewVersion: string; onPreviewModeChange: (preview: boolean) => void;
  onSave: (path: string, content: string, expectedRevision: number) => Promise<number>; onRename: () => void; onDirtyChange: (dirty: boolean) => void;
}) {
  const [value, setValue] = useState("");
  const [status, setStatus] = useState<"saved" | "dirty" | "saving">("saved");
  const timer = useRef<number | null>(null);
  const pathRef = useRef<string | null>(null);
  const revisionRef = useRef(0);

  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current);
    pathRef.current = file?.path ?? null;
    revisionRef.current = file?.revision ?? 0;
    setValue(file?.kind === "text" ? file.content as string : "");
    setStatus("saved");
    onDirtyChange(false);
  }, [file?.projectId, file?.chatId, file?.path, file?.kind, file?.revision, onDirtyChange]);

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  const change = (next = "") => {
    if (!file || file.kind !== "text") return;
    setValue(next);
    setStatus("dirty");
    onDirtyChange(true);
    if (timer.current) window.clearTimeout(timer.current);
    const savePath = file.path;
    timer.current = window.setTimeout(async () => {
      if (pathRef.current !== savePath) return;
      setStatus("saving");
      try {
        revisionRef.current = await onSave(savePath, next, revisionRef.current);
        setStatus("saved");
        onDirtyChange(false);
      } catch {
        setStatus("dirty");
        onDirtyChange(true);
      }
    }, 650);
  };

  const openPreview = async () => {
    if (!file || file.kind !== "text") return;
    if (status === "dirty") {
      if (timer.current) window.clearTimeout(timer.current);
      setStatus("saving");
      try {
        revisionRef.current = await onSave(file.path, value, revisionRef.current);
        setStatus("saved");
        onDirtyChange(false);
      } catch {
        setStatus("dirty");
        onDirtyChange(true);
        return;
      }
    }
    onPreviewModeChange(true);
  };

  if (!file) return <section className="editor-pane empty-editor"><Braces size={36} /><h2>Sélectionne un fichier</h2><p>Les fichiers de cette tâche restent dans ton navigateur.</p></section>;
  const canPreview = file.kind === "text" && /\.html?$/i.test(file.path);
  return (
    <section className="editor-pane">
      <header className="editor-header"><div className="editor-tabs"><button className={`editor-tab ${!previewMode ? "active" : ""}`} onClick={() => onPreviewModeChange(false)}><Code2 size={13} /><span>{file.path.split("/").at(-1)}</span></button>{canPreview && <button className={`editor-tab preview-tab ${previewMode ? "active" : ""}`} onClick={() => void openPreview()}><Eye size={13} /><span>Aperçu</span></button>}</div><div className="editor-meta"><span className={`save-status ${status}`}><Save size={12} />{status === "saved" ? "Enregistré" : status === "saving" ? "Enregistrement…" : "Modifié"}</span><button className="icon-button compact" onClick={onRename} aria-label="Renommer"><Pencil size={14} /></button></div></header>
      <div className="editor-path">{file.path}<span>rév. {file.revision}</span></div>
      {previewMode && canPreview ? <HtmlPreview file={file} files={files} version={previewVersion} /> : file.kind === "binary" ? <BinaryPreview file={file} /> : <Editor height="100%" path={`${file.projectId}/${file.chatId}/${file.path}`} language={languageForPath(file.path)} theme="codex-dark" value={value} onChange={change} options={{
        minimap: { enabled: false }, fontSize: 13, lineHeight: 21, fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', monospace",
        padding: { top: 14 }, scrollBeyondLastLine: false, smoothScrolling: true, automaticLayout: true,
        wordWrap: "on", readOnly, renderLineHighlight: "line", overviewRulerBorder: false, hideCursorInOverviewRuler: true,
        bracketPairColorization: { enabled: true }, guides: { bracketPairs: true, indentation: true },
      }} />}
    </section>
  );
}
